/** Stationery builder state: generalInfo, frontDesign, backDesign, theme, canvas, save status */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type {
  StationeryInvitation,
  StationeryTemplateData,
  StationeryBuilderTab,
  CanvasState,
  HostField,
  PaperSize,
  CardType,
  StationeryInvitationStatus,
} from '@/types/stationery-invitation'
import {
  DEFAULT_TEMPLATE_DATA,
  DEFAULT_PAPER_SIZE,
  DEFAULT_CARD_TYPE,
} from '@/types/stationery-invitation'
import { saveStationery as saveStationeryAPI } from '@/services/stationery-invitations/api'
import { logger } from '@/lib/logger'

const STORAGE_KEY = 'dday-stationery-builder-storage'

/**
 * Known stationery font values (CSS font-family strings) keyed by normalized
 * name for fast lookup. Used to fix kebab-case or misformatted font values
 * before passing to the template package (whose font loader doesn't handle
 * kebab-case → see template package font normalize issue).
 */
const KNOWN_FONTS: Record<string, string> = {
  'cormorant garamond': 'Cormorant Garamond, serif',
  lora: 'Lora, serif',
  merriweather: 'Merriweather, serif',
  'crimson text': 'Crimson Text, serif',
  'playfair display': 'Playfair Display, serif',
  'source serif pro': 'Source Serif Pro, serif',
  bitter: 'Bitter, serif',
  nunito: 'Nunito, sans-serif',
  lato: 'Lato, sans-serif',
  'dancing script': 'Dancing Script, cursive',
  inter: 'Inter, sans-serif',
  montserrat: 'Montserrat, sans-serif',
  raleway: 'Raleway, sans-serif',
  'josefin sans': 'Josefin Sans, sans-serif',
  oswald: 'Oswald, sans-serif',
  cinzel: 'Cinzel, serif',
}

/**
 * Ensure a font value is a valid CSS font-family string. Handles kebab-case
 * slugs (e.g. "cormorant-garamond") that the template package font loader
 * can't resolve, converting them to the proper "Cormorant Garamond, serif".
 */
function sanitizeFontValue(value: string | undefined): string | undefined {
  if (!value) return value
  // Already a valid CSS font-family (contains a comma with fallback)
  if (value.includes(',')) return value
  // Normalize: kebab-case → spaces, lowercase
  const normalized = value.replace(/-/g, ' ').trim().toLowerCase()
  return KNOWN_FONTS[normalized] ?? value
}

// ============================================================================
// HELPER: Deep Path Update
// ============================================================================

/** Set nested value by dot path (e.g. 'generalInfo.hostInfo.fields.0.name'). */
function setPath<T extends object>(obj: T, path: string, value: unknown): T {
  const keys = path.split('.')
  const result = { ...obj } as Record<string, unknown>
  let current = result

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]
    const nextKey = keys[i + 1]
    const isNextKeyIndex = !isNaN(Number(nextKey))

    if (current[key] === undefined || current[key] === null) {
      current[key] = isNextKeyIndex ? [] : {}
    } else if (Array.isArray(current[key])) {
      current[key] = [...(current[key] as unknown[])]
    } else if (typeof current[key] === 'object') {
      current[key] = { ...(current[key] as object) }
    }

    current = current[key] as Record<string, unknown>
  }

  const lastKey = keys[keys.length - 1]
  current[lastKey] = value

  return result as T
}

// ============================================================================
// STORE TYPES
// ============================================================================

interface StationeryBuilderState {
  // --------------------------------------------------------------------------
  // DATA
  // --------------------------------------------------------------------------
  /** Stationery invitation ID (client UUID for new, backend ID after save) */
  stationeryId: string | null
  /** Whether stationery has been persisted to backend (vs just local UUID) */
  isPersistedToBackend: boolean
  /** Linked digital invitation ID (if created from invitation) */
  linkedInvitationId: string | null
  /** User-friendly title */
  title: string
  /** Paper size – builder only supports 5x7. */
  paperSize: PaperSize
  /** Card type: flat or folded. */
  cardType: CardType
  /** Template data for preview (passed to template package) */
  templateData: StationeryTemplateData
  /** Current status */
  status: StationeryInvitationStatus

  // --------------------------------------------------------------------------
  // UI STATE
  // --------------------------------------------------------------------------
  /** Currently active tab */
  activeTab: StationeryBuilderTab
  /** Canvas state (page, zoom, guides) */
  canvas: CanvasState
  /** Whether there are unsaved changes */
  isSaved: boolean
  /** Whether the store has been initialized */
  isInitialized: boolean
  /** Whether we're currently saving */
  isSaving: boolean

  // --------------------------------------------------------------------------
  // ACTIONS - Initialization
  // --------------------------------------------------------------------------
  /** Generate UUID for new stationery if not exists (for image uploads before save) */
  initializeStationeryId: () => string
  initializeNew: (options?: {
    paperSize?: PaperSize
    cardType?: CardType
    designId?: string
    linkedInvitationId?: string
  }) => void
  loadExisting: (stationery: StationeryInvitation) => void
  prefillFromInvitation: (invitationData: PrefillData) => void
  resetStore: () => void

  // --------------------------------------------------------------------------
  // ACTIONS - Template Data Updates (using dot-notation paths)
  // --------------------------------------------------------------------------
  /** Update any value in templateData using dot-notation path */
  updateTemplatePath: (path: string, value: unknown) => void
  /** Update multiple paths at once */
  updateTemplatePaths: (updates: Record<string, unknown>) => void

  // --------------------------------------------------------------------------
  // ACTIONS - Convenience Methods
  // --------------------------------------------------------------------------
  setTitle: (title: string) => void
  setPaperSize: (size: PaperSize) => void
  setCardType: (type: CardType) => void
  setActiveTab: (tab: StationeryBuilderTab) => void

  // Canvas
  setActivePage: (page: 'front' | 'back') => void
  setZoom: (zoom: number) => void
  toggleBleedLines: () => void
  toggleSafeZone: () => void

  // Front Design
  setFrontDesign: (designId: string) => void
  setFrontImage: (imageUrl: string) => void
  setFrontImages: (images: Array<{ imageUrl: string }>) => void
  removeFrontImage: (index: number) => void

  // Host fields (generalInfo.hostInfo.fields)
  addHostField: (field: HostField) => void
  updateHostField: (index: number, field: HostField) => void
  removeHostField: (index: number) => void
  reorderHostFields: (fields: HostField[]) => void

  // State management
  markAsSaved: () => void
  markAsUnsaved: () => void
  setIsSaving: (isSaving: boolean) => void

  // Save to backend (init or update) with optional progress callback
  saveStationery: (
    onProgress?: (step: string, progress: number) => void
  ) => Promise<StationeryInvitation>

  // Clear builder and localStorage
  clearBuilder: () => void

  // URL replacement (for image transfer)
  replaceImageUrls: (urlMap: Record<string, string>) => void

  // Getters
  getTemplateData: () => StationeryTemplateData
  hasUnsavedChanges: () => boolean
}

/** Digital invitation → stationery prefill (hero → frontDesign, etc.). */
interface PrefillData {
  title?: string // Digital invitation title
  // Front design (matches hero)
  designId?: string
  images?: Array<{ imageUrl?: string; alt?: string; caption?: string }>
  layout?: 'top' | 'center' | 'bottom' | 'left' | 'right'
  enableDate?: boolean
  showDesignTitle?: boolean
  trimToEdge?: boolean
  // Color theme (maps to frontDesign.colorTheme)
  colorTheme?: string
  // General info
  eventDateTime?: string
  timezone?: string
  venueName?: string
  address?: string
  hostFields?: HostField[]
  // Greetings (goes to backDesign)
  greetingTitle?: string
  /** Single string from digital invitation - will be wrapped in array */
  greetingMessage?: string
  // Fonts (for back page)
  contentFontFamily?: string
  systemFontFamily?: string
  // QR code
  invitationUrl?: string
  qrCodeUrl?: string
  linkedInvitationId?: string
  /** Back-of-card visit URL slug (invitation liveUrlSlug). */
  invitationSlug?: string
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialCanvasState: CanvasState = {
  activePage: 'front',
  zoom: 100,
  showBleedLines: false,
  showSafeZone: false,
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useStationeryBuilderStore = create<StationeryBuilderState>()(
  devtools(
    persist(
      (set, get) => ({
        // --------------------------------------------------------------------------
        // INITIAL STATE
        // --------------------------------------------------------------------------
        stationeryId: null,
        isPersistedToBackend: false,
        linkedInvitationId: null,
        title: '',
        paperSize: DEFAULT_PAPER_SIZE,
        cardType: DEFAULT_CARD_TYPE,
        templateData: { ...DEFAULT_TEMPLATE_DATA },
        status: 'draft',
        activeTab: 'front',
        canvas: { ...initialCanvasState },
        isSaved: true,
        isInitialized: false,
        isSaving: false,

        // --------------------------------------------------------------------------
        // INITIALIZATION ACTIONS
        // --------------------------------------------------------------------------

        initializeStationeryId: () => {
          const current = get().stationeryId
          if (current) return current
          const newId = crypto.randomUUID()
          set(
            (prev) => ({
              stationeryId: newId,
              isSaved: prev.isSaved,
            }),
            false,
            'stationeryBuilder/initializeStationeryId'
          )
          return newId
        },

        initializeNew: (options) => {
          const newId = crypto.randomUUID()

          // Generate default date: 7 days from now at 12:00 PM
          const defaultDate = new Date()
          defaultDate.setDate(defaultDate.getDate() + 7)
          defaultDate.setHours(12, 0, 0, 0)
          const defaultEventDateTime = defaultDate.toISOString()
          const defaultTimezone =
            Intl.DateTimeFormat().resolvedOptions().timeZone

          // Builder only supports 5×7; ignore 4x6 from URL/options
          const paperSize: PaperSize = '5x7'
          const cardType: CardType = options?.cardType ?? DEFAULT_CARD_TYPE

          set(
            {
              stationeryId: newId,
              isPersistedToBackend: false,
              linkedInvitationId: options?.linkedInvitationId || null,
              title: '',
              paperSize,
              cardType,
              templateData: {
                ...DEFAULT_TEMPLATE_DATA,
                generalInfo: {
                  ...DEFAULT_TEMPLATE_DATA.generalInfo,
                  dateTime: {
                    eventDateTime: defaultEventDateTime,
                    timezone: defaultTimezone,
                  },
                },
                frontDesign: {
                  ...DEFAULT_TEMPLATE_DATA.frontDesign,
                  selectedDesign:
                    options?.designId ||
                    DEFAULT_TEMPLATE_DATA.frontDesign.selectedDesign,
                },
                backDesign: DEFAULT_TEMPLATE_DATA.backDesign
                  ? {
                      ...DEFAULT_TEMPLATE_DATA.backDesign,
                      qrCode: {
                        ...DEFAULT_TEMPLATE_DATA.backDesign.qrCode,
                      },
                    }
                  : undefined,
              },
              status: 'draft',
              activeTab: 'front',
              canvas: { ...initialCanvasState },
              isSaved: false,
              isInitialized: true,
              isSaving: false,
            },
            false,
            'stationeryBuilder/initializeNew'
          )
        },

        loadExisting: (stationery: StationeryInvitation) => {
          const paperSize: PaperSize = '5x7'
          const td = stationery.templateData as {
            cardType?: CardType
            paperType?: CardType
          }
          const cardType: CardType =
            td?.cardType ?? td?.paperType ?? DEFAULT_CARD_TYPE
          // Preserve invitationSlug so Or visit URL survives save/reload
          const templateData: StationeryTemplateData = {
            generalInfo: stationery.templateData.generalInfo,
            frontDesign: stationery.templateData.frontDesign,
            ...(stationery.templateData.backDesign != null && {
              backDesign: stationery.templateData.backDesign,
            }),
            ...(stationery.templateData.invitationSlug != null && {
              invitationSlug: stationery.templateData.invitationSlug,
            }),
          }
          set(
            {
              stationeryId: stationery.id,
              isPersistedToBackend: true,
              linkedInvitationId: stationery.invitationId,
              title: stationery.title,
              paperSize,
              cardType,
              templateData,
              status: stationery.status,
              activeTab: 'front',
              canvas: { ...initialCanvasState },
              isSaved: true,
              isInitialized: true,
              isSaving: false,
            },
            false,
            'stationeryBuilder/loadExisting'
          )
        },

        /** Prefill from digital invitation (hostInfo, dateTime, hero → frontDesign, greetings → backDesign). */
        prefillFromInvitation: (data: PrefillData) => {
          const current = get().templateData
          const hasQrPrefill =
            (data.qrCodeUrl != null && data.qrCodeUrl !== '') ||
            !!data.invitationUrl

          set(
            {
              title: data.title || get().title,
              linkedInvitationId:
                data.linkedInvitationId ?? get().linkedInvitationId ?? null,
              templateData: {
                ...current,
                generalInfo: {
                  hostInfo: {
                    fields:
                      data.hostFields || current.generalInfo.hostInfo.fields,
                  },
                  dateTime: {
                    eventDateTime:
                      data.eventDateTime ||
                      current.generalInfo.dateTime.eventDateTime,
                    timezone:
                      data.timezone || current.generalInfo.dateTime.timezone,
                  },
                  location: {
                    venueName:
                      data.venueName || current.generalInfo.location.venueName,
                    address:
                      data.address || current.generalInfo.location.address,
                  },
                },
                frontDesign: {
                  ...current.frontDesign,
                  colorTheme: data.colorTheme || current.frontDesign.colorTheme,
                  selectedDesign:
                    data.designId || current.frontDesign.selectedDesign,
                  images: data.images ?? current.frontDesign.images,
                  layout: data.layout ?? current.frontDesign.layout,
                  showDesignTitle:
                    data.showDesignTitle ?? current.frontDesign.showDesignTitle,
                  enableDate: data.enableDate ?? current.frontDesign.enableDate,
                  trimToEdge: data.trimToEdge ?? current.frontDesign.trimToEdge,
                },
                backDesign: {
                  ...current.backDesign,
                  greetings: {
                    title:
                      data.greetingTitle ||
                      current.backDesign?.greetings?.title,
                    message: data.greetingMessage
                      ? [data.greetingMessage]
                      : current.backDesign?.greetings?.message,
                  },
                  qrCode: hasQrPrefill
                    ? {
                        ...current.backDesign?.qrCode,
                        enabled: true,
                        qrImageUrl: data.qrCodeUrl ?? data.invitationUrl ?? '',
                      }
                    : current.backDesign?.qrCode,
                  contentFontFamily: sanitizeFontValue(
                    data.contentFontFamily ||
                      current.backDesign?.contentFontFamily
                  ),
                  systemFontFamily: sanitizeFontValue(
                    data.systemFontFamily ||
                      current.backDesign?.systemFontFamily
                  ),
                  greetingTitleSize: current.backDesign?.greetingTitleSize,
                  greetingMessageSize: current.backDesign?.greetingMessageSize,
                  showDateTime: current.backDesign?.showDateTime,
                  dateTimeSize: current.backDesign?.dateTimeSize,
                  showLocation: current.backDesign?.showLocation,
                  locationSize: current.backDesign?.locationSize,
                  showFrom: current.backDesign?.showFrom,
                  showHostLabels: current.backDesign?.showHostLabels,
                  hostNameSize: current.backDesign?.hostNameSize,
                },
                ...(data.invitationSlug != null && {
                  invitationSlug: data.invitationSlug,
                }),
              },
              isSaved: false,
            },
            false,
            'stationeryBuilder/prefillFromInvitation'
          )
        },

        resetStore: () => {
          set(
            {
              stationeryId: null,
              isPersistedToBackend: false,
              linkedInvitationId: null,
              title: '',
              paperSize: DEFAULT_PAPER_SIZE,
              cardType: DEFAULT_CARD_TYPE,
              templateData: { ...DEFAULT_TEMPLATE_DATA },
              status: 'draft',
              activeTab: 'front',
              canvas: { ...initialCanvasState },
              isSaved: true,
              isInitialized: false,
              isSaving: false,
            },
            false,
            'stationeryBuilder/resetStore'
          )
        },

        // --------------------------------------------------------------------------
        // TEMPLATE DATA UPDATE ACTIONS
        // --------------------------------------------------------------------------

        updateTemplatePath: (path: string, value: unknown) => {
          set(
            (state) => ({
              templateData: setPath(state.templateData, path, value),
              isSaved: false,
            }),
            false,
            'stationeryBuilder/updateTemplatePath'
          )
        },

        updateTemplatePaths: (updates: Record<string, unknown>) => {
          set(
            (state) => {
              let newTemplateData = state.templateData
              for (const [path, value] of Object.entries(updates)) {
                newTemplateData = setPath(newTemplateData, path, value)
              }
              return {
                templateData: newTemplateData,
                isSaved: false,
              }
            },
            false,
            'stationeryBuilder/updateTemplatePaths'
          )
        },

        // --------------------------------------------------------------------------
        // CONVENIENCE ACTIONS
        // --------------------------------------------------------------------------

        setTitle: (title: string) => {
          set({ title, isSaved: false }, false, 'stationeryBuilder/setTitle')
        },

        setPaperSize: (size: PaperSize) => {
          set(
            { paperSize: size, isSaved: false },
            false,
            'stationeryBuilder/setPaperSize'
          )
        },

        setCardType: (type: CardType) => {
          set(
            { cardType: type, isSaved: false },
            false,
            'stationeryBuilder/setCardType'
          )
        },

        setActiveTab: (tab: StationeryBuilderTab) => {
          const state = get()
          let activePage = state.canvas.activePage
          if (tab === 'front') activePage = 'front'
          if (tab === 'back') activePage = 'back'

          set(
            {
              activeTab: tab,
              canvas: { ...state.canvas, activePage },
            },
            false,
            'stationeryBuilder/setActiveTab'
          )
        },

        // Canvas
        setActivePage: (page: 'front' | 'back') => {
          set(
            (state) => ({
              canvas: { ...state.canvas, activePage: page },
            }),
            false,
            'stationeryBuilder/setActivePage'
          )
        },

        setZoom: (zoom: number) => {
          const clampedZoom = Math.max(25, Math.min(200, zoom))
          set(
            (state) => ({
              canvas: { ...state.canvas, zoom: clampedZoom },
            }),
            false,
            'stationeryBuilder/setZoom'
          )
        },

        toggleBleedLines: () => {
          set(
            (state) => ({
              canvas: {
                ...state.canvas,
                showBleedLines: !state.canvas.showBleedLines,
              },
            }),
            false,
            'stationeryBuilder/toggleBleedLines'
          )
        },

        toggleSafeZone: () => {
          set(
            (state) => ({
              canvas: {
                ...state.canvas,
                showSafeZone: !state.canvas.showSafeZone,
              },
            }),
            false,
            'stationeryBuilder/toggleSafeZone'
          )
        },

        // Front Design
        setFrontDesign: (designId: string) => {
          set(
            (state) => ({
              templateData: {
                ...state.templateData,
                frontDesign: {
                  ...state.templateData.frontDesign,
                  selectedDesign: designId,
                  images: [], // Reset images — designs have different count/orientation requirements
                },
              },
              isSaved: false,
            }),
            false,
            'stationeryBuilder/setFrontDesign'
          )
        },

        /**
         * Set front image (first image in images array)
         * Matches digital invitation's hero.images structure
         */
        setFrontImage: (imageUrl: string) => {
          set(
            (state) => {
              const currentImages = state.templateData.frontDesign.images || []
              const newImages =
                imageUrl === ''
                  ? [] // Clear images
                  : currentImages.length > 0
                    ? [{ ...currentImages[0], imageUrl }] // Update first image
                    : [{ imageUrl }] // Create new first image

              return {
                templateData: {
                  ...state.templateData,
                  frontDesign: {
                    ...state.templateData.frontDesign,
                    images: newImages,
                  },
                },
                isSaved: false,
              }
            },
            false,
            'stationeryBuilder/setFrontImage'
          )
        },

        /**
         * Set multiple front images at once (for designs requiring 2+ images)
         */
        setFrontImages: (images: Array<{ imageUrl: string }>) => {
          set(
            (state) => ({
              templateData: {
                ...state.templateData,
                frontDesign: {
                  ...state.templateData.frontDesign,
                  images: images.map((img) => ({ imageUrl: img.imageUrl })),
                },
              },
              isSaved: false,
            }),
            false,
            'stationeryBuilder/setFrontImages'
          )
        },

        /**
         * Remove a front image by index
         */
        removeFrontImage: (index: number) => {
          set(
            (state) => {
              const currentImages = state.templateData.frontDesign.images || []
              const newImages = currentImages.filter((_, i) => i !== index)
              return {
                templateData: {
                  ...state.templateData,
                  frontDesign: {
                    ...state.templateData.frontDesign,
                    images: newImages,
                  },
                },
                isSaved: false,
              }
            },
            false,
            'stationeryBuilder/removeFrontImage'
          )
        },

        // Host fields
        addHostField: (field: HostField) => {
          set(
            (state) => ({
              templateData: {
                ...state.templateData,
                generalInfo: {
                  ...state.templateData.generalInfo,
                  hostInfo: {
                    fields: [
                      ...state.templateData.generalInfo.hostInfo.fields,
                      field,
                    ],
                  },
                },
              },
              isSaved: false,
            }),
            false,
            'stationeryBuilder/addHostField'
          )
        },

        updateHostField: (index: number, field: HostField) => {
          set(
            (state) => {
              const fields = [...state.templateData.generalInfo.hostInfo.fields]
              fields[index] = field
              return {
                templateData: {
                  ...state.templateData,
                  generalInfo: {
                    ...state.templateData.generalInfo,
                    hostInfo: { fields },
                  },
                },
                isSaved: false,
              }
            },
            false,
            'stationeryBuilder/updateHostField'
          )
        },

        removeHostField: (index: number) => {
          set(
            (state) => ({
              templateData: {
                ...state.templateData,
                generalInfo: {
                  ...state.templateData.generalInfo,
                  hostInfo: {
                    fields:
                      state.templateData.generalInfo.hostInfo.fields.filter(
                        (_, i) => i !== index
                      ),
                  },
                },
              },
              isSaved: false,
            }),
            false,
            'stationeryBuilder/removeHostField'
          )
        },

        reorderHostFields: (fields: HostField[]) => {
          set(
            (state) => ({
              templateData: {
                ...state.templateData,
                generalInfo: {
                  ...state.templateData.generalInfo,
                  hostInfo: { fields },
                },
              },
              isSaved: false,
            }),
            false,
            'stationeryBuilder/reorderHostFields'
          )
        },

        // State management
        markAsSaved: () =>
          set({ isSaved: true }, false, 'stationeryBuilder/markAsSaved'),
        markAsUnsaved: () =>
          set({ isSaved: false }, false, 'stationeryBuilder/markAsUnsaved'),
        setIsSaving: (isSaving: boolean) =>
          set({ isSaving }, false, 'stationeryBuilder/setIsSaving'),

        // Save stationery to backend
        saveStationery: async (onProgress) => {
          const {
            stationeryId,
            isPersistedToBackend,
            title,
            paperSize,
            cardType,
            templateData,
            linkedInvitationId,
          } = get()

          set(
            { isSaving: true },
            false,
            'stationeryBuilder/saveStationery/start'
          )

          try {
            let saveId = stationeryId

            // If not yet persisted, initialize first
            if (!isPersistedToBackend) {
              onProgress?.('saving', 5)

              // Dynamic import to reduce initial bundle size
              const { initStationery } =
                await import('@/services/stationery-invitations/api')

              const templateDataForApi: StationeryTemplateData = {
                ...templateData,
                paperSize,
                cardType,
                backDesign: templateData.backDesign
                  ? {
                      ...templateData.backDesign,
                      contentFontFamily: sanitizeFontValue(
                        templateData.backDesign.contentFontFamily
                      ),
                      systemFontFamily: sanitizeFontValue(
                        templateData.backDesign.systemFontFamily
                      ),
                    }
                  : undefined,
              }
              const initResult = await initStationery({
                paperSize,
                templateData: templateDataForApi,
                title: title?.trim() || undefined,
                invitationId: linkedInvitationId || undefined,
              })

              saveId = initResult.id

              set(
                {
                  stationeryId: saveId,
                  isPersistedToBackend: true,
                },
                false,
                'stationeryBuilder/saveStationery/initialized'
              )
            }

            // Step 1: Generate print files in parallel (10-40%)
            onProgress?.('generating', 10)

            let frontBlob: Blob | undefined
            let backBlob: Blob | undefined

            try {
              // Dynamic import to reduce initial bundle size
              const { exportForPrint } =
                await import('@kiltechco/dday-templates/stationery')

              const templateDataForPrint = {
                ...templateData,
                paperSize: '5x7' as const,
                cardType,
                backDesign: templateData.backDesign
                  ? {
                      ...templateData.backDesign,
                      contentFontFamily: sanitizeFontValue(
                        templateData.backDesign.contentFontFamily
                      ),
                      systemFontFamily: sanitizeFontValue(
                        templateData.backDesign.systemFontFamily
                      ),
                    }
                  : undefined,
              }
              // Suppress harmless console.error noise from html-to-image
              // trying to read cross-origin stylesheets (Google Sign-In,
              // Google Fonts CDN). The library handles these internally and
              // still embeds all same-origin @fontsource fonts correctly.
              const origConsoleError = console.error
              console.error = (...args: unknown[]) => {
                const msg = String(args[0] ?? '')
                if (
                  msg.includes('Error inlining remote css') ||
                  msg.includes('Error loading remote stylesheet') ||
                  msg.includes('Error fetching remote css') ||
                  msg.includes('Error while reading CSS rules') ||
                  msg.includes('SecurityError') ||
                  msg.includes('fonts.googleapis.com')
                )
                  return
                origConsoleError.apply(console, args)
              }

              let frontResult, backResult
              try {
                ;[frontResult, backResult] = await Promise.all([
                  exportForPrint(templateDataForPrint, 'front', {
                    paperSize: '5x7',
                    dpi: 300,
                    format: 'jpeg',
                    quality: 1.0,
                  }),
                  exportForPrint(templateDataForPrint, 'back', {
                    paperSize: '5x7',
                    dpi: 300,
                    format: 'jpeg',
                    quality: 1.0,
                  }),
                ])
              } finally {
                console.error = origConsoleError
              }

              frontBlob = frontResult.blob
              backBlob = backResult.blob

              onProgress?.('generated', 40)
            } catch (printError) {
              logger.warn('Stationery print file generation failed', printError)
              frontBlob = undefined
              backBlob = undefined
            }

            // Step 2: Upload to backend with unified endpoint (50-90%)
            onProgress?.('uploading', 50)

            const templateDataForApi: StationeryTemplateData = {
              ...templateData,
              paperSize,
              cardType,
              backDesign: templateData.backDesign
                ? {
                    ...templateData.backDesign,
                    contentFontFamily: sanitizeFontValue(
                      templateData.backDesign.contentFontFamily
                    ),
                    systemFontFamily: sanitizeFontValue(
                      templateData.backDesign.systemFontFamily
                    ),
                  }
                : undefined,
            }
            const finalStationery = await saveStationeryAPI(
              saveId!,
              templateDataForApi,
              title?.trim() || undefined,
              frontBlob,
              backBlob
            )

            onProgress?.('finalizing', 90)

            set(
              {
                stationeryId: finalStationery.id,
                isPersistedToBackend: true,
                status: finalStationery.status,
                isSaved: true,
                isSaving: false,
              },
              false,
              'stationeryBuilder/saveStationery/success'
            )

            return finalStationery
          } catch (error) {
            set(
              { isSaving: false },
              false,
              'stationeryBuilder/saveStationery/error'
            )
            throw error
          }
        },

        clearBuilder: () => {
          set(
            {
              stationeryId: null,
              isPersistedToBackend: false,
              linkedInvitationId: null,
              title: '',
              paperSize: DEFAULT_PAPER_SIZE,
              cardType: DEFAULT_CARD_TYPE,
              templateData: { ...DEFAULT_TEMPLATE_DATA },
              status: 'draft',
              activeTab: 'front',
              canvas: { ...initialCanvasState },
              isSaved: true,
              isInitialized: false,
              isSaving: false,
            },
            false,
            'stationeryBuilder/clearBuilder'
          )
          setTimeout(() => localStorage.removeItem(STORAGE_KEY), 0)
        },

        replaceImageUrls: (urlMap: Record<string, string>) => {
          if (Object.keys(urlMap).length === 0) return

          const currentData = get().templateData
          let serialized = JSON.stringify(currentData)

          for (const [oldUrl, newUrl] of Object.entries(urlMap)) {
            serialized = serialized.replaceAll(oldUrl, newUrl)
          }

          const updatedData = JSON.parse(serialized) as StationeryTemplateData

          set(
            { templateData: updatedData },
            false,
            'stationeryBuilder/replaceImageUrls'
          )
        },

        getTemplateData: () => get().templateData,
        hasUnsavedChanges: () => !get().isSaved,
      }),
      {
        name: STORAGE_KEY,
        partialize: (state) => ({
          stationeryId: state.stationeryId,
          isPersistedToBackend: state.isPersistedToBackend,
          linkedInvitationId: state.linkedInvitationId,
          title: state.title,
          paperSize: state.paperSize,
          cardType: state.cardType,
          templateData: state.templateData,
          status: state.status,
          isSaved: state.isSaved,
          isInitialized: state.isInitialized,
        }),
        // Merge persisted state with current state, handling schema migrations
        merge: (persistedState, currentState) => {
          const persisted = persistedState as Partial<StationeryBuilderState>
          const persistedFrontDesign = persisted.templateData?.frontDesign as
            | (typeof DEFAULT_TEMPLATE_DATA.frontDesign & {
                photo?: { imageUrl?: string; alt?: string }
                showDateOnFront?: boolean
              })
            | undefined
          const persistedBackDesign = persisted.templateData?.backDesign

          // Handle photo → images migration (still needed for initial stationery creation)
          let migratedImages = persistedFrontDesign?.images
          if (!migratedImages && persistedFrontDesign?.photo?.imageUrl) {
            migratedImages = [
              {
                imageUrl: persistedFrontDesign.photo.imageUrl,
                alt: persistedFrontDesign.photo.alt || '',
              },
            ]
          }

          // Handle showDateOnFront → enableDate migration (still needed for initial stationery creation)
          const migratedEnableDate =
            persistedFrontDesign?.enableDate ??
            persistedFrontDesign?.showDateOnFront ??
            DEFAULT_TEMPLATE_DATA.frontDesign.enableDate

          // Handle greetings.message string → string[] migration
          const persistedGreetings = persistedBackDesign?.greetings
          let migratedMessage = persistedGreetings?.message
          if (typeof migratedMessage === 'string') {
            migratedMessage = migratedMessage ? [migratedMessage] : ['']
          }

          // Migrate paperSize 4x6 → 5x7; default cardType to flat when missing (support legacy paperType)
          const migratedPaperSize: PaperSize = '5x7'
          const persistedTd = persisted.templateData as {
            cardType?: CardType
            paperType?: CardType
          }
          const migratedCardType: CardType =
            persistedTd?.cardType ?? persistedTd?.paperType ?? DEFAULT_CARD_TYPE

          // Deep merge templateData (only keys template reads; no paperSize, cardType, front, back, title, version)
          const mergedTemplateData: StationeryTemplateData = {
            generalInfo: {
              hostInfo: {
                ...DEFAULT_TEMPLATE_DATA.generalInfo.hostInfo,
                ...(persisted.templateData?.generalInfo?.hostInfo || {}),
              },
              dateTime: {
                ...DEFAULT_TEMPLATE_DATA.generalInfo.dateTime,
                ...(persisted.templateData?.generalInfo?.dateTime || {}),
              },
              location: {
                ...DEFAULT_TEMPLATE_DATA.generalInfo.location,
                ...(persisted.templateData?.generalInfo?.location || {}),
              },
            },
            frontDesign: {
              colorTheme:
                persistedFrontDesign?.colorTheme ??
                DEFAULT_TEMPLATE_DATA.frontDesign.colorTheme,
              selectedDesign:
                persistedFrontDesign?.selectedDesign ||
                DEFAULT_TEMPLATE_DATA.frontDesign.selectedDesign,
              images:
                migratedImages ?? DEFAULT_TEMPLATE_DATA.frontDesign.images,
              showDesignTitle:
                persistedFrontDesign?.showDesignTitle ??
                DEFAULT_TEMPLATE_DATA.frontDesign.showDesignTitle,
              enableDate: migratedEnableDate,
              trimToEdge:
                persistedFrontDesign?.trimToEdge ??
                DEFAULT_TEMPLATE_DATA.frontDesign.trimToEdge,
            },
            backDesign: {
              greetings: {
                title:
                  persistedGreetings?.title ??
                  DEFAULT_TEMPLATE_DATA.backDesign?.greetings?.title,
                message:
                  migratedMessage ??
                  DEFAULT_TEMPLATE_DATA.backDesign?.greetings?.message,
              },
              greetingTitleSize:
                persistedBackDesign?.greetingTitleSize ??
                DEFAULT_TEMPLATE_DATA.backDesign?.greetingTitleSize,
              greetingMessageSize:
                persistedBackDesign?.greetingMessageSize ??
                DEFAULT_TEMPLATE_DATA.backDesign?.greetingMessageSize,
              showDateTime:
                persistedBackDesign?.showDateTime ??
                DEFAULT_TEMPLATE_DATA.backDesign?.showDateTime,
              dateTimeSize:
                persistedBackDesign?.dateTimeSize ??
                DEFAULT_TEMPLATE_DATA.backDesign?.dateTimeSize,
              showLocation:
                persistedBackDesign?.showLocation ??
                DEFAULT_TEMPLATE_DATA.backDesign?.showLocation,
              locationSize:
                persistedBackDesign?.locationSize ??
                DEFAULT_TEMPLATE_DATA.backDesign?.locationSize,
              showFrom:
                persistedBackDesign?.showFrom ??
                DEFAULT_TEMPLATE_DATA.backDesign?.showFrom,
              showHostLabels:
                persistedBackDesign?.showHostLabels ??
                DEFAULT_TEMPLATE_DATA.backDesign?.showHostLabels,
              hostNameSize:
                persistedBackDesign?.hostNameSize ??
                DEFAULT_TEMPLATE_DATA.backDesign?.hostNameSize,
              qrCode: {
                ...DEFAULT_TEMPLATE_DATA.backDesign?.qrCode,
                ...(persistedBackDesign?.qrCode || {}),
              },
              contentFontFamily: sanitizeFontValue(
                persistedBackDesign?.contentFontFamily ??
                  DEFAULT_TEMPLATE_DATA.backDesign?.contentFontFamily
              ),
              systemFontFamily: sanitizeFontValue(
                persistedBackDesign?.systemFontFamily ??
                  DEFAULT_TEMPLATE_DATA.backDesign?.systemFontFamily
              ),
            },
            // Preserve invitationSlug so the "Or visit" URL below the QR code survives page refresh
            ...(typeof persisted.templateData?.invitationSlug === 'string' && {
              invitationSlug: persisted.templateData.invitationSlug,
            }),
          }

          return {
            ...currentState,
            ...persisted,
            paperSize: migratedPaperSize,
            cardType: migratedCardType,
            templateData: mergedTemplateData,
          }
        },
      }
    ),
    { name: 'DDay-StationeryBuilderStore' }
  )
)
